import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DocumentUpload from '@/components/documents/DocumentUpload';
import DocumentList from '@/components/documents/DocumentList';
import * as documentsApi from '@/services/documentsService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Home, Building2, User, Bed } from 'lucide-react';
import StorageUsageIndicator from '@/components/documents/StorageUsageIndicator';
import { fetchHospedajes } from '@/services/hospedajes.service';

export default function HospedajeDocumentsPage() {
  const { idHospedaje } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hospedaje, setHospedaje] = useState(null);
  const [loadingHospedaje, setLoadingHospedaje] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      setLoading(true);
      const data = await documentsApi.listDocuments({ ownerType: 'hospedaje', ownerId: idHospedaje });
      setItems(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (idHospedaje) load();
  }, [idHospedaje]);

  useEffect(() => {
    const loadHospedaje = async () => {
      if (!idHospedaje) return;
      setLoadingHospedaje(true);
      try {
        const { data } = await fetchHospedajes({ limit: 1, offset: 1, query: { _id: idHospedaje } });
        setHospedaje(Array.isArray(data) && data.length ? data[0] : null);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingHospedaje(false);
      }
    };
    loadHospedaje();
  }, [idHospedaje]);

  const handleUploaded = () => load();
  const handleDownload = async (id) => {
    try {
      const url = await documentsApi.getPresignedDownload({ id });
      window.open(url, '_blank');
    } catch (e) {
      console.error(e);
    }
  };
  const handleDelete = async (id) => {
    try {
      await documentsApi.deleteDocument({ id });
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const getGestionBadge = (tipo, valor) => {
    if (!valor || valor === 'No aplica') return null;
    
    const colors = {
      'Autonoma': 'bg-green-100 text-green-800',
      'Central': 'bg-blue-100 text-blue-800',
      'Camarera Propietario': 'bg-purple-100 text-purple-800',
      'camarera pagada por el propietario': 'bg-orange-100 text-orange-800',
      'Directa': 'bg-yellow-100 text-yellow-800'
    };

    return (
      <Badge variant="secondary" className={colors[valor] || 'bg-gray-100 text-gray-800'}>
        {tipo}: {valor}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="px-2">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="text-sm text-muted-foreground">
          <Link to="/documentacion" className="hover:underline">Documentación</Link>
          <span className="mx-1">/</span>
          <Link to="/documentacion/hospedajes" className="hover:underline">Hospedajes</Link>
          <span className="mx-1">/</span>
          <span className="text-foreground">Detalle</span>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="h-5 w-5 text-green-500" />
            Documentos del Hospedaje
          </CardTitle>
          <div className="mt-1 text-sm text-muted-foreground">
            {loadingHospedaje ? (
              <span>Cargando información del hospedaje...</span>
            ) : hospedaje ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">{hospedaje.nombrePropiedad || 'Sin nombre'}</span>
                  <Badge variant="outline">
                    {hospedaje.tipo === 'vertical' ? 'Vertical' : 'Horizontal'}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    <span>{hospedaje.edificio?.nombre || 'N/D'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    <span>{hospedaje.coordinador?.nombre || 'N/D'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Bed className="h-3 w-3" />
                    <span>{hospedaje.numeroCamas || 0} camas • {hospedaje.numeroHabitaciones || 0} habitaciones</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-2">
                  {getGestionBadge('Lencería', hospedaje.gestionLenceria)}
                  {getGestionBadge('Aseo', hospedaje.gestionAseo)}
                  {hospedaje.tipoRenta && (
                    <Badge variant="secondary" className="bg-indigo-100 text-indigo-800">
                      {hospedaje.tipoRenta}
                    </Badge>
                  )}
                </div>
              </div>
            ) : (
              <span>No se encontró información del hospedaje.</span>
            )}
          </div>
          <StorageUsageIndicator className="mt-3" />
        </CardHeader>
        <CardContent>
          <DocumentUpload ownerType="hospedaje" ownerId={idHospedaje} onUploaded={handleUploaded} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Documentos Subidos</CardTitle>
          <p className="text-sm text-muted-foreground">
            Contratos, inventarios, reportes de mantenimiento y otros documentos relacionados con este hospedaje.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Cargando documentos...</div>
          ) : (
            <DocumentList items={items} onDownload={handleDownload} onDelete={handleDelete} />
          )}
        </CardContent>
      </Card>

      {/* Secciones de documentos específicos */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Contratos y Legales</CardTitle>
            <p className="text-sm text-muted-foreground">
              Contratos de arrendamiento, seguros, documentos legales.
            </p>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              Sube aquí los contratos específicos de este hospedaje.
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Inventarios</CardTitle>
            <p className="text-sm text-muted-foreground">
              Inventarios de mobiliario, lencería y amenities.
            </p>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              Documentos de inventario y control de activos.
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Mantenimiento</CardTitle>
            <p className="text-sm text-muted-foreground">
              Reportes de mantenimiento, reparaciones, inspecciones.
            </p>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              Historial de mantenimientos y reparaciones realizadas.
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Certificaciones</CardTitle>
            <p className="text-sm text-muted-foreground">
              Certificados de habitabilidad, seguridad, servicios.
            </p>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              Certificaciones y permisos requeridos para operación.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
